import {
  ActionList,
  ActionListItemDescriptor,
  Button,
  LegacyCard,
  LegacyStack,
  Popover,
  Text,
} from "@shopify/polaris";
import { CaretDownMinor } from "@shopify/polaris-icons";
import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPurchaseStateWithCustomerRelationship } from "@smartrr/shared/entities/PurchaseState";
import { RRuleManager } from "@smartrr/shared/entities/Schedule/RRuleManager";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { SubscriptionContractSubscriptionStatus } from "@smartrr/shared/shopifyGraphQL/api";
import { areDatesOnSameDay } from "@smartrr/shared/utils/dateUtils";
import {
  isCPSPrepaid,
  isCPSRecentlyMigratedPrepaid,
  isLastBillingCycleOfCPS,
} from "@smartrr/shared/utils/isPrepaid";
import { getSellingPlansForVariants } from "@smartrr/shared/utils/sellingPlans";
import {
  DateConfirmationModal,
  useDateConfirmationModalStore,
} from "@vendor-app/app/_sharedComponents/DateConfirmationModal";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import { useDisabledDatesByBillingDays } from "@vendor-app/app/AdminRoute/AdminSubscriptionDetailsRoute/hooks/useDisabledDatesByBillingDays";
import { useSendOrderNowAvailability } from "@vendor-app/utils/useSendOrderNowAvailability";
import React, { useCallback, useMemo, useState } from "react";

import {
  SellingPlanSelectorContainer,
  getFormattedUpcomingDateForSubDetails,
  getUpcomingTransactionDateAsString,
  isSavedByVariant,
} from "../../../libs";
import { useNextDelivery } from "../../../libs/hooks/useNextDelivery";
import { useSellingPlans } from "../../../libs/providers/SellingPlansProvider";
import { useSubscriptionPurchasables } from "../../../libs/providers/SubscriptionPurchasablesProvider";
import { ConfirmSellingPlanModal, SetNextOrderDateModal, ShowBundleContentModal } from "../../modals";
import { useSetNextOrderModalStore } from "../../modals/SetNextOrderDateModal/useSetNextOrderModalStore";
import { SellingPlanSelect } from "../SellingPlanSelect";
import { useSetNextDate } from "../SubscriptionTabs/hooks/useSetNextDate";
import { useSkip } from "../SubscriptionTabs/hooks/useSkip";
import { useConfirmSellingPlanModal } from "../../modals/ConfirmSellingPlanModal/useConfirmSellingPlanModal";
import { purchaseStateContainsAnchor } from "@smartrr/shared/utils/anchors";

interface INextOrderProps {
  organization: IOrganization | null;
  sellingPlanGroup: ISmartrrSellingPlanGroup | undefined;
  areActionsDisabled: boolean;
  rruleManager: RRuleManager | undefined;
  customerPurchaseState: IPurchaseStateWithCustomerRelationship;
}

export const NextOrderInfo = ({
  customerPurchaseState,
  areActionsDisabled,
  rruleManager,
  sellingPlanGroup,
}: INextOrderProps) => {
  const { openModal: openSetNextOrderDateModal } = useSetNextOrderModalStore();
  const { openModal: openDateConfirmationModal } = useDateConfirmationModalStore();
  const { externalSubscriptionStatus, schedule, purchaseStateStatus } = customerPurchaseState;
  const { variantToPurchasableMap } = useSubscriptionPurchasables();
  const organization = useActiveOrganizationSelector();
  const { sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId, sellingPlanGroups, sequentialGroupIds } =
    useSellingPlans();
  const { nextDelivery } = useNextDelivery(customerPurchaseState);

  const openSetNextOrderModal = () => openSetNextOrderDateModal({});

  const [isOpenNextOrderOptions, setOpenNextOrderOptions] = useState(false);

  const sendNowAvailable = useSendOrderNowAvailability(nextDelivery ?? undefined);

  const omittedDates = useMemo(() => {
    if (customerPurchaseState.subProperties?.sendNow) {
      return [customerPurchaseState.nextBillingDate, customerPurchaseState.nextOrderDate];
    }
    return [];
  }, [customerPurchaseState.nextBillingDate, customerPurchaseState.nextOrderDate]);

  const disabledDatesFromBilling = useDisabledDatesByBillingDays(
    customerPurchaseState.nextBillingDate,
    omittedDates
  );

  const disabledDatesFromDelivery = useDisabledDatesByBillingDays(
    customerPurchaseState.unpauseDate ?? customerPurchaseState.nextOrderDate,
    omittedDates
  );
  const isPrepaid = useMemo(() => isCPSPrepaid(schedule), [schedule]);

  const onSkip = useSkip(customerPurchaseState);
  const onSetNextDate = useSetNextDate(customerPurchaseState);

  const isMigrating = useMemo(
    () => externalSubscriptionStatus === SubscriptionContractSubscriptionStatus.Active,
    []
  );
  const isRecentlyMigratedPrepaid = useMemo(
    () => isCPSRecentlyMigratedPrepaid(customerPurchaseState),
    [customerPurchaseState]
  );

  const onNextDateSelect = useCallback(async (date: Date) => {
    await onSetNextDate(date).then(() => {
      const currentDate = new Date();

      if (areDatesOnSameDay(date, currentDate)) {
        openDateConfirmationModal({ sendNowDate: date });
      }
    });
  }, []);

  const isCPSOnLastBillingCycle = useMemo(() => {
    return isLastBillingCycleOfCPS(schedule);
  }, [schedule]);

  const hasPackageProtection = useMemo(() => {
    return customerPurchaseState.stLineItems.some(lineItem => isSavedByVariant(lineItem.vnt));
  }, [customerPurchaseState.stLineItems]);

  const prepaidSellingPlanIds = useMemo(() => {
    if (!hasPackageProtection) {
      return new Set<number>();
    }

    const prepaidIds = new Set<number>();
    for (const group of sellingPlanGroups) {
      for (const plan of group.sellingPlans) {
        if (plan.billingPolicy.intervalCount !== plan.deliveryPolicy.intervalCount) {
          prepaidIds.add(plan.shopifyNumericId);
        }
      }
    }

    return prepaidIds;
  }, [hasPackageProtection, sellingPlanGroups]);

  const sellingPlanOptions = useMemo(() => {
    const allOptions = getSellingPlansForVariants(
      customerPurchaseState.stLineItems
        .filter(lineItem => !!lineItem.vnt && !lineItem.isAddOn && !isSavedByVariant(lineItem.vnt))
        .map(lineItem => lineItem.vnt!)
        .filter(Boolean),
      sellingPlanGroupsByVariantId,
      sellingPlanGroupsByProductId,
      variantToPurchasableMap
    );

    if (!hasPackageProtection) {
      return allOptions;
    }

    return allOptions.filter(plan => {
      const planIsPrepaid = prepaidSellingPlanIds.has(plan.id);
      const planIsSequential = sequentialGroupIds.has(plan.group_id);
      return !planIsPrepaid && !planIsSequential;
    });
  }, [
    customerPurchaseState,
    sellingPlanGroupsByVariantId,
    sellingPlanGroupsByProductId,
    variantToPurchasableMap,
    hasPackageProtection,
    prepaidSellingPlanIds,
    sequentialGroupIds,
  ]);

  const hasAnchors = purchaseStateContainsAnchor(customerPurchaseState);

  const nextBillingDateFormatted = useMemo(() => {
    return getUpcomingTransactionDateAsString(customerPurchaseState, organization, disabledDatesFromBilling);
  }, [organization, customerPurchaseState, disabledDatesFromBilling]);

  const nextOrderDateFormatted = useMemo(() => {
    return getFormattedUpcomingDateForSubDetails(customerPurchaseState, organization, disabledDatesFromDelivery);
  }, [organization, customerPurchaseState, disabledDatesFromDelivery]);

  const { sellingPlanUpdating, confirmSellingPlanModalProps } = useConfirmSellingPlanModal(
    customerPurchaseState,
    sellingPlanOptions
  );

  const optionsList = useMemo(() => {
    const optionsList: ActionListItemDescriptor[] = [];

    if (nextDelivery && !isRecentlyMigratedPrepaid && !areActionsDisabled) {
      optionsList.push({
        id: "subscription-details__skip-action",
        content: "Skip",
        onAction: () => onSkip(nextDelivery.date),
      });
    }

    if (nextDelivery && !areActionsDisabled) {
      optionsList.push({
        id: "subscription-details__set-next-order-date-action",
        content: "Set next order date",
        onAction: openSetNextOrderModal,
        disabled: isMigrating || purchaseStateStatus !== SubscriptionContractSubscriptionStatus.Active,
      });
    }
    return optionsList;
  }, [
    nextDelivery,
    sendNowAvailable,
    purchaseStateStatus,
    isMigrating,
    isRecentlyMigratedPrepaid,
    areActionsDisabled,
  ]);
  const orderDate = useMemo(
    () => (isPrepaid ? customerPurchaseState.nextOrderDate : customerPurchaseState.nextBillingDate),
    [isPrepaid, customerPurchaseState.nextOrderDate, customerPurchaseState.nextBillingDate]
  );

  return (
    <LegacyCard.Section>
      <LegacyStack distribution="equalSpacing">
        <LegacyStack vertical spacing="extraTight">
          <Text variant="bodyMd" as="p" id="subscription-details__next-order-date">
            Next order: {nextOrderDateFormatted}
          </Text>
          {!isPrepaid || !isCPSOnLastBillingCycle ? (
            <Text variant="bodyMd" as="p" color="subdued" id="subscription-details__next-billing-date">
              Next bill: <span>{nextBillingDateFormatted}</span>
            </Text>
          ) : null}
        </LegacyStack>
        {optionsList.length ? (
          <Popover
            active={isOpenNextOrderOptions}
            onClose={() => setOpenNextOrderOptions(false)}
            activator={
              <Button
                id="subscription-details__next-order-options"
                disabled={areActionsDisabled}
                icon={CaretDownMinor}
                size="slim"
                onClick={() => setOpenNextOrderOptions(a => !a)}
              >
                Options
              </Button>
            }
          >
            <ActionList items={optionsList} onActionAnyItem={() => setOpenNextOrderOptions(false)} />
          </Popover>
        ) : null}
      </LegacyStack>

      <LegacyStack>
        <SellingPlanSelectorContainer>
          <SellingPlanSelect
            nextDelivery={nextDelivery}
            areActionsDisabled={areActionsDisabled}
            customerPurchaseState={customerPurchaseState}
            rruleManager={rruleManager}
            sellingPlanOptions={sellingPlanOptions}
            sellingPlanGroup={sellingPlanGroup}
            sellingPlanUpdating={sellingPlanUpdating}
          />
        </SellingPlanSelectorContainer>
        {confirmSellingPlanModalProps ? (
          <ConfirmSellingPlanModal
            open={confirmSellingPlanModalProps.open}
            planName={confirmSellingPlanModalProps.planName}
            onClose={confirmSellingPlanModalProps.onClose}
            onConfirm={confirmSellingPlanModalProps.onConfirm}
            selectedSellingPlanId={confirmSellingPlanModalProps.selectedSellingPlanId}
            nextOrderDate={confirmSellingPlanModalProps.nextOrderDate}
            isLoading={confirmSellingPlanModalProps.isLoading}
          />
        ) : null}
      </LegacyStack>

      {/* Set next billing date for cps */}
      {orderDate ? (
        <SetNextOrderDateModal
          initialISODate={orderDate}
          onConfirm={onNextDateSelect}
          allowSendNow={!hasAnchors}
        />
      ) : (
        <React.Fragment />
      )}

      {/* Confirm if upcoming delivery will be skipped */}
      {nextDelivery ? (
        <DateConfirmationModal customerPurchaseState={customerPurchaseState} onConfirm={onSkip} />
      ) : (
        <React.Fragment />
      )}

      <ShowBundleContentModal />
    </LegacyCard.Section>
  );
};
